import { isUndefined } from 'lodash-es';

import { Platform } from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

interface IActiveTieredDeliveryFeeProps {
  platform: Platform;
  subTotalCents: number;
}

interface ITieredFee {
  discount: number;
  fee: number;
  serviceFeePercent: number;
  smallCartFee: number;
}

const defaultValue = {
  threshold: 0,
  freeDeliveryThreshold: 0,
  smallCartFeeThreshold: 0,
  tier: {
    discount: 0,
    fee: 0,
    serviceFeePercent: 0,
    smallCartFee: 0,
  },
};

export const useActiveTieredDeliveryFees = ({
  platform,
  subTotalCents,
}: IActiveTieredDeliveryFeeProps): {
  freeDeliveryThreshold: number;
  threshold: number;
  smallCartFeeThreshold: number;
  tier: ITieredFee;
} => {
  const feeTiersFlag = useFlag(LaunchDarklyFlag.TIERED_DELIVERY_FEES);

  // Return "default" tier if flag is null
  if (!feeTiersFlag || typeof feeTiersFlag !== 'object') {
    return defaultValue;
  }

  const feeTiers = feeTiersFlag as Record<string, Record<string, ITieredFee>>;

  let highestTier = 0;
  let freeDeliveryThreshold;
  // Find the delivery tier that match the subtotal
  // Find Small Cart Fee threshold & Delivery Fee threshold
  let smallCartFeeThreshold = 0;
  for (const key of Object.keys(feeTiers)) {
    const tier: number = +key;
    if (
      tier <= subTotalCents &&
      tier >= highestTier &&
      (platform in feeTiers[tier] || Platform.WEB in feeTiers[tier])
    ) {
      highestTier = tier;
    }
    // Return the first tier of which the smallCartFee is 0 or doesn't exist
    if (!feeTiers[key]?.[platform]?.smallCartFee && smallCartFeeThreshold === 0) {
      smallCartFeeThreshold = tier;
    }

    // tier needed to reach in order to get free delivery
    if (
      feeTiers[key]?.[platform]?.fee - feeTiers[key]?.[platform]?.discount === 0 &&
      isUndefined(freeDeliveryThreshold)
    ) {
      freeDeliveryThreshold = tier;
    }
  }

  if (feeTiers[highestTier]?.[platform]) {
    // Find the "threshold" ( next tier after the highestTier )
    const keys = Object.keys(feeTiers);
    const nextIndex = keys.indexOf(highestTier.toString()) + 1;
    const threshold = Number(keys[nextIndex] ?? highestTier);

    // Return threshold and current delivery tier
    return {
      freeDeliveryThreshold: freeDeliveryThreshold ?? 0,
      threshold,
      smallCartFeeThreshold,
      tier: feeTiers[highestTier]?.[platform],
    };
  }
  // If the tier doesn't exist return a "default" tier
  return defaultValue;
};
